import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Badge,
  Form,
  Spinner,
} from "react-bootstrap";
import {
  getGlobalFeatureFlags,
  updateGlobalFeatureFlag,
  getGlobalFeatureFlagAccounts,
  updateAccountFeatureFlags,
} from "../../actions/feature-flag.action";
import { getAccountsList } from "../../actions/global-account.action";
import CustomInput from "../../components/inputs/new-input";
import useFeatureFlag from "../../hooks/useFeatureFlag";

const FeatureFlagsPage = ({ user }) => {
  const dispatch = useDispatch();
  const [flags, setFlags] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [flagAccounts, setFlagAccounts] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingFlagKey, setSavingFlagKey] = useState(null);
  const [error, setError] = useState(null);

  const isGlobalUser = user?.global_user;
  
  // Demo feature flag - show beta announcement banner
  const showBetaAnnouncement = useFeatureFlag('beta_announcement_banner');

  useEffect(() => {
    if (!isGlobalUser) return;
    loadData();
  }, [isGlobalUser]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [flagsRes, accountsRes] = await Promise.all([
        dispatch(getGlobalFeatureFlags()),
        dispatch(getAccountsList()),
      ]);
      const loadedFlags = flagsRes || [];
      const loadedAccounts = accountsRes || [];
      setFlags(loadedFlags);
      setAccounts(loadedAccounts);

      const accountPromises = loadedFlags.map((flag) =>
        dispatch(getGlobalFeatureFlagAccounts(flag.key))
          .then((res) => ({ key: flag.key, accounts: (res?.data || res) || [] }))
          .catch(() => ({ key: flag.key, accounts: [] }))
      );
      const accountResults = await Promise.all(accountPromises);
      const accountsByFlag = {};
      accountResults.forEach(({ key, accounts }) => {
        accountsByFlag[key] = accounts;
      });
      setFlagAccounts(accountsByFlag);
    } catch (err) {
      setError("Failed to load feature flags.");
    } finally {
      setLoading(false);
    }
  };

  const filteredFlags = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return flags;
    return flags.filter(
      (flag) =>
        flag.key?.toLowerCase().includes(term) ||
        flag.label?.toLowerCase().includes(term) ||
        flag.description?.toLowerCase().includes(term) ||
        flag.category?.toLowerCase().includes(term)
    );
  }, [flags, search]);

  const enabledCount = useMemo(
    () => flags.filter((flag) => flag.value).length,
    [flags]
  );
  const betaCount = useMemo(
    () => flags.filter((flag) => flag.is_beta).length,
    [flags]
  );
  const overrideCount = useMemo(
    () =>
      Object.values(flagAccounts).filter((accountList) =>
        accountList.some((account) => account.override)
      ).length,
    [flagAccounts]
  );

  const handleGlobalToggle = async (flag, newValue) => {
    setSavingFlagKey(flag.key);
    try {
      await dispatch(updateGlobalFeatureFlag(flag.key, newValue));
      setFlags((prev) =>
        prev.map((f) => (f.key === flag.key ? { ...f, value: newValue } : f))
      );
    } catch (err) {
      setError(`Failed to update ${flag.label}.`);
    } finally {
      setSavingFlagKey(null);
    }
  };

  const loadFlagAccounts = async (flagKey) => {
    try {
      const res = await dispatch(getGlobalFeatureFlagAccounts(flagKey));
      setFlagAccounts((prev) => ({
        ...prev,
        [flagKey]: (res?.data || res) || [],
      }));
    } catch (err) {
      setError(`Failed to load accounts for ${flagKey}.`);
    }
  };

  const handleToggleFlagAccount = async (flag, account) => {
    const newValue = !account.override;
    setSavingFlagKey(flag.key);
    setFlagAccounts((prev) => {
      const current = prev[flag.key] || [];
      return {
        ...prev,
        [flag.key]: current.map((a) =>
          a.id === account.id ? { ...a, override: newValue, resolved: newValue } : a
        ),
      };
    });
    try {
      await dispatch(
        updateAccountFeatureFlags(account.id, { [flag.key]: newValue })
      );
      await loadFlagAccounts(flag.key);
    } catch (err) {
      setError(`Failed to update override for ${account.name}.`);
      await loadFlagAccounts(flag.key);
    } finally {
      setSavingFlagKey(null);
    }
  };

  const handleAddFlagAccount = async (flag, accountId) => {
    if (!accountId) return;
    setSavingFlagKey(flag.key);
    try {
      await dispatch(
        updateAccountFeatureFlags(accountId, { [flag.key]: true })
      );
      await loadFlagAccounts(flag.key);
    } catch (err) {
      setError(`Failed to add override for ${flag.label}.`);
    } finally {
      setSavingFlagKey(null);
    }
  };

  if (!isGlobalUser) {
    return (
      <div className="purchase-order-block">
        <p className="text-muted">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="purchase-order-block">
      <div className="row mb-3">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <h2 className="content-heading content-heading-lg text-uppercase my-0">
            Feature Flags
          </h2>
        </div>
      </div>

      {/* Demo Feature: Beta Announcement Banner */}
      {showBetaAnnouncement && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-info d-flex align-items-center" role="alert">
              <div className="me-3">
                <span style={{ fontSize: '1.5rem' }}>🚀</span>
              </div>
              <div>
                <h5 className="alert-heading mb-1">Beta Features Now Available!</h5>
                <p className="mb-0">
                  Exciting new beta features are now enabled for your account. 
                  This is a demo feature controlled by the <code>beta_announcement_banner</code> feature flag.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-3 mb-2">
          <div className="border rounded p-3 bg-white">
            <div className="text-uppercase text-muted small">Total</div>
            <div className="h3 mb-0 text-primary">{flags.length}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="border rounded p-3 bg-white">
            <div className="text-uppercase text-muted small">Enabled</div>
            <div className="h3 mb-0 text-success">{enabledCount}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="border rounded p-3 bg-white">
            <div className="text-uppercase text-muted small">Beta</div>
            <div className="h3 mb-0 text-warning">{betaCount}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="border rounded p-3 bg-white">
            <div className="text-uppercase text-muted small">With Overrides</div>
            <div className="h3 mb-0 text-info">{overrideCount}</div>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6 col-lg-4">
          <CustomInput
            name="search"
            placeholder="Search by name, key, or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Accordion alwaysOpen>
          {filteredFlags.map((flag) => (
            <AccordionItem eventKey={flag.key} key={flag.key}>
              <AccordionHeader>
                <div className="d-flex w-100 align-items-center justify-content-between pe-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-semibold">{flag.label}</span>
                    <Badge bg="light" text="dark" className="font-monospace">
                      {flag.key}
                    </Badge>
                    {flag.category && (
                      <Badge bg="secondary">{flag.category}</Badge>
                    )}
                    {flag.is_beta && <Badge bg="warning">Beta</Badge>}
                    {(() => {
                      const onCount = (flagAccounts[flag.key] || []).filter(
                        (a) => a.override
                      ).length;
                      if (onCount === 0) return null;
                      return (
                        <Badge bg="info" title={`${onCount} account override(s) ON`}>
                          {onCount} ON
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <Form.Switch
                      id={`global-toggle-${flag.key}`}
                      label="Global Default"
                      checked={!!flag.value}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleGlobalToggle(flag, e.target.checked)
                      }
                      disabled={savingFlagKey === flag.key}
                    />
                    {savingFlagKey === flag.key && (
                      <Spinner animation="border" size="sm" />
                    )}
                  </div>
                </div>
              </AccordionHeader>
              <AccordionBody>
                <p className="text-dark">{flag.description}</p>

                <div className="border rounded p-3 bg-light">
                  <div className="mt-0">
                    <h6 className="content-heading content-heading-sm text-uppercase mb-3">
                      Accounts with Overrides
                    </h6>

                    {(() => {
                      const flagAccountList = flagAccounts[flag.key] || [];

                      return (
                        <>
                          {flagAccountList.length === 0 ? (
                            <p className="text-muted small mb-0">
                              No account overrides for this flag.
                            </p>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm table-borderless align-middle mb-0">
                                <tbody>
                                  {flagAccountList.map((account) => (
                                    <tr key={account.id}>
                                      <td className="ps-0" style={{ width: "60%" }}>
                                        {account.name}
                                      </td>
                                      <td>
                                        <Form.Switch
                                          id={`flag-${flag.key}-account-${account.id}`}
                                          label={account.override ? "On" : "Off"}
                                          checked={!!account.override}
                                          onChange={() =>
                                            handleToggleFlagAccount(flag, account)
                                          }
                                          disabled={savingFlagKey === flag.key}
                                        />
                                      </td>
                                      <td className="text-end pe-0">
                                        {account.resolved ? (
                                          <Badge bg="success">Resolved ON</Badge>
                                        ) : (
                                          <Badge bg="danger">Resolved OFF</Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          <div className="row align-items-end mt-3">
                            <div className="col-md-5">
                              <Form.Select
                                value=""
                                onChange={(e) =>
                                  handleAddFlagAccount(flag, e.target.value)
                                }
                                disabled={savingFlagKey === flag.key}
                              >
                                <option value="">Select account to activate...</option>
                                {accounts.map((account) => (
                                  <option value={account.id} key={account.id}>
                                    {account.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </AccordionBody>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.userData,
});

export default connect(mapStateToProps)(FeatureFlagsPage);
